/* Plantica — Header

   Sticky itself is CSS, applied to the section wrapper (see
   plantica-header.css). This element only hides the header while scrolling down
   and brings it back on the way up.

   Deliberately an autonomous custom element, not the theme's
   `<header is="sticky-header">`: customised built-in elements are not supported
   in Safari and the theme ships no polyfill, so the theme's own sticky header
   does nothing there. This one works everywhere.

   If the script never runs, the header is still sticky and still visible — the
   class this adds is the only thing that hides it. */

(() => {
  if (customElements.get('plantica-header')) return;

  const HIDDEN = 'is-hidden';

  /* The header's state is published on <html>, the way the theme publishes its
     own with `header-pinned`. Anything that has to keep clear of the header can
     then follow it in CSS alone — the sticky product gallery does. Without this
     the gallery has to reserve the header's height permanently, which on a
     laptop pushes its thumbnails below the fold even while the header is away. */
  const ROOT_HIDDEN = 'plantica-header-hidden';
  const THRESHOLD = 8; // ignore sub-pixel and rubber-band scrolls

  class PlanticaHeader extends HTMLElement {
    // Watched so flipping the setting in the customizer takes effect without a
    // reload — the editor changes the attribute in place.
    static get observedAttributes() {
      return ['data-sticky'];
    }

    lastY = window.scrollY;
    ticking = false;
    listening = false;

    /* Published as a variable so nothing downstream has to hard-code a number
       that changes with the logo, the chip row or the viewport. */
    publishHeight = () => {
      document.documentElement.style.setProperty('--plantica-header-height', this.offsetHeight + 'px');
    };

    /* A class field, not a method bound in connectedCallback. On upgrade the
       browser runs the constructor, then attributeChangedCallback, and only
       then connectedCallback — so binding later meant the attribute callback
       had already registered the UNBOUND prototype method as the listener.
       Called with `this` set to window, the arithmetic came out NaN, no class
       was toggled and nothing threw: the header simply never hid. */
    onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;

      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - this.lastY;

        if (Math.abs(delta) > THRESHOLD) {
          // Named drawers only. `.drawer[open]` also matches the newsletter
          // popup, which this store keeps open, and the header would then never
          // hide at all. Never hide at the very top either — nothing to gain.
          const drawerOpen =
            document.querySelector('#MenuDrawer[open], #CartDrawer[open], #SearchDrawer[open]') !== null;

          const hide = delta > 0 && y > this.offsetHeight && !drawerOpen;
          this.classList.toggle(HIDDEN, hide);
          document.documentElement.classList.toggle(ROOT_HIDDEN, hide);
          this.lastY = y;
        }

        this.ticking = false;
      });
    };

    connectedCallback() {
      this.lastY = window.scrollY;
      this.publishHeight();
      window.addEventListener('resize', this.publishHeight, { passive: true });
      this.sync();
    }

    attributeChangedCallback() {
      this.sync();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.publishHeight);
      document.documentElement.classList.remove(ROOT_HIDDEN);
      this.listening = false;
    }

    sync() {
      const wanted =
        this.dataset.sticky === 'on-scroll-up' &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (wanted === this.listening) return;

      if (wanted) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
      } else {
        window.removeEventListener('scroll', this.onScroll);
        this.classList.remove(HIDDEN);
        document.documentElement.classList.remove(ROOT_HIDDEN);
      }

      this.listening = wanted;
    }
  }

  customElements.define('plantica-header', PlanticaHeader);
})();

/* The category row's ends (05.10.2026, the client's request)

   On a phone the row scrolls sideways and nothing said so. Each end with more
   chips behind it gets a white fade and a black arrow — `can-prev` and
   `can-next` on the row, which the CSS turns into the arrows: at the start
   only the right one, at the end only the left one, both in between. A tap on
   an arrow moves the row by most of its width.

   Measured again whenever the row scrolls or changes size; a row that fits
   shows neither, which is the desktop. Without this script the row still
   scrolls, as it always did — only the hint is missing. */

(() => {
  if (customElements.get('plantica-header-chips')) return;

  const EDGE = 2; // px of slack, for sub-pixel scroll positions

  class PlanticaHeaderChips extends HTMLElement {
    measure = () => {
      const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
      this.classList.toggle('can-prev', scrollLeft > EDGE);
      this.classList.toggle('can-next', scrollLeft < scrollWidth - clientWidth - EDGE);
    };

    step = (event) => {
      const direction = Number(event.currentTarget.dataset.chipsStep) || 1;
      this.viewport.scrollBy({ left: direction * this.viewport.clientWidth * 0.7, behavior: 'smooth' });
    };

    connectedCallback() {
      this.viewport = this.querySelector('[data-chips-viewport]');
      if (!this.viewport) return;

      this.viewport.addEventListener('scroll', this.measure, { passive: true });
      this.querySelectorAll('[data-chips-step]').forEach((button) => button.addEventListener('click', this.step));
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this.viewport);
      this.measure();
    }

    disconnectedCallback() {
      this.viewport?.removeEventListener('scroll', this.measure);
      this.querySelectorAll('[data-chips-step]').forEach((button) => button.removeEventListener('click', this.step));
      this.resizeObserver?.disconnect();
    }
  }

  customElements.define('plantica-header-chips', PlanticaHeaderChips);
})();

/* The account icon on a touch screen (09.10.2026)

   The theme (theme.js) cancels every tap on a touch screen and replays it as a
   click — but only when the tapped node has a link or a button among its
   ancestors in the page. The account icon is Shopify's <shopify-account>: its
   link lives in the element's own shadow DOM, out of that search, so the tap
   was cancelled, never replayed, and nothing happened. Kept from the theme's
   handler, the tap ends in the browser's own click, which reaches the link
   just as a mouse click does. */

(() => {
  const keepTap = (event) => event.stopPropagation();

  const wire = () => {
    document.querySelectorAll('.plantica-header__account').forEach((account) => {
      if (account.dataset.planticaTap) return;
      account.dataset.planticaTap = 'native';
      account.addEventListener('touchend', keepTap, { passive: true });
    });
  };

  wire();
  document.addEventListener('shopify:section:load', wire);
})();
